import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * The catalogue search behind `businesses/catalog/search`.
 *
 * One endpoint answers query, filters, sort and paging together, and the same
 * search backs Item Master. It lives here rather than inside the page so the
 * two cannot end up asking for the catalogue in two slightly different ways.
 */

export interface CatalogSearchModel {
  Query: string;
  Page: number;
  Size: number;
  /** Tags for the built-in filters, ids for the ones the business defines. */
  Filters: string[];
  Sort: number;
}

/** `businesses/catalog/search` rows. */
export interface CatalogProduct {
  id: string;
  gtin: string | null;
  internalId: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  completeName: string | null;
  itemImage: string | null;
  /** The free-text terms this item also answers to. */
  searchTags: string | null;
}

export function useCatalogSearch(model: CatalogSearchModel) {
  return useQuery({
    queryKey: ['catalog-search', model],
    queryFn: () =>
      unwrap<{ items: CatalogProduct[]; count: number }>(
        api.post('businesses/catalog/search', model)
      ),
    placeholderData: keepPreviousData,
  });
}

/** A filter the business has defined, on top of the built-in tags. */
export interface CatalogFilter {
  id: string;
  name: string | null;
  tag?: string | null;
}

export function useCatalogFilters() {
  return useQuery({
    queryKey: ['catalog-search', 'filters'],
    queryFn: () =>
      unwrap<{ results: CatalogFilter[] }>(
        api.get<ApiEnvelope<{ results: CatalogFilter[] }>>(
          'businesses/catalog/filters'
        )
      ),
    select: (data) => data.results ?? [],
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * The filters that ship with every catalogue, in the order the original listed
 * them. They are sent as tags; the business's own filters are sent as ids.
 */
export const BUILT_IN_FILTERS = [
  'NoPictures',
  'HasPicture',
  'HasVideo',
  'PackagingSensitive',
  'TemperatureSensitive',
  'AlcoholicBeverage',
  'IdRequired',
  'Ebt',
  'NotEbt',
  'Perishable',
] as const;

/** `Sort` — the eight the search offers. */
export const CATALOG_SORTS = [
  { value: 0, labelKey: 'searchOptimization.sortDefault' },
  { value: 1, labelKey: 'searchOptimization.sortAZ' },
  { value: 2, labelKey: 'searchOptimization.sortZA' },
  { value: 7, labelKey: 'searchOptimization.sortRankDesc' },
  { value: 10, labelKey: 'searchOptimization.sortRankAsc' },
  { value: 6, labelKey: 'searchOptimization.sortEditedAt' },
  { value: 11, labelKey: 'searchOptimization.sortTopSellingDesc' },
  { value: 12, labelKey: 'searchOptimization.sortTopSellingAsc' },
] as const;

/**
 * Save one item's search tags.
 *
 * The endpoint takes the whole item, so the edited copy is posted rather than
 * a patch — which is also why the drawer starts from the row it was opened on.
 */
export function useUpdateCatalogItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: CatalogProduct) =>
      unwrap<unknown>(api.post(`businesses/catalog/${item.id}/update`, item)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalog-search'] });
    },
  });
}
